import { css } from '@emotion/react';
import {
  ActionIcon,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Container,
  Flex,
  Grid,
  Group,
  Image,
  Paper,
  Radio,
  Rating,
  Table,
  Tabs,
  Text,
  useMantineTheme,
  createStyles,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useLocation, Link, useNavigate } from "react-router-dom";
import { is_mobile_view } from '../../config';
import { 
  IconArrowRight, 
  IconMinus, 
  IconPlus, 
  IconTrash,
  IconReceipt2,
  IconTrophy,
  IconShare,
  IconMapPin,
  IconUser,
  IconMessage2,
  IconLogout,
  IconChevronRight
} from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { api } from '../../services/master.service';
import { showErrorToast, showSuccessToast } from '../../utilities/Toast';
import Orders from './Orders';
import Addresses from './Addresses';
import AccountDetails from './AccountDetails';
import Feedback from './Feedback';
import Streak from './Streaks';
import ReferralCoupon from './ReferralCoupon';

const useStyles = createStyles((theme) => ({
  mobileNavScroll: {
    padding: '4px 10px 16px 10px',
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    backgroundColor: theme.white,
    '&::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  },

  navChip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 16px',
    borderRadius: 20,
    minWidth: 85,
    backgroundColor: theme.colors.gray[0],
    border: `1.5px solid transparent`,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    '&:active': { transform: 'scale(0.95)' },
  },

  activeNavChip: {
    backgroundColor: theme.colors.brand[0],
    borderColor: theme.colors.brand[2],
    boxShadow: `0 8px 16px -4px ${theme.colors.brand[1]}`,
  },

  chipIcon: {
    width: 24,
    height: 24,
    marginBottom: 6,
    color: theme.colors.gray[6],
    transition: 'color 0.2s ease',
  },

  activeChipIcon: {
    color: theme.colors.brand[7],
  },

  chipLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.colors.gray[6],
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },

  activeChipLabel: {
    color: theme.colors.brand[9],
    fontWeight: 800,
  },

  webSidebarContainer: {
    width: '100%',
    backgroundColor: theme.colors.gray[0],
    borderRadius: 24,
    padding: 12,
    height: '100%',
    minHeight: 600,
    position: 'sticky',
    top: 180,
  },

  webNavButton: {
    width: '100%',
    padding: '16px 20px',
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    backgroundColor: 'transparent',
    marginBottom: 4,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.03)',
    },
  },

  activeWebNavButton: {
    backgroundColor: theme.white,
    boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
    '& .nav-icon': {
      color: theme.colors.brand[6],
    },
    '& .nav-text': {
      color: theme.colors.brand[9],
      fontWeight: 800,
    },
  },

  navIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.white,
    transition: 'all 0.2s ease',
    color: theme.colors.gray[5],
  },

  activeNavIconBox: {
    backgroundColor: theme.colors.brand[0],
    color: theme.colors.brand[6],
  },

  contentArea: {
    width: '100%',
    backgroundColor: theme.white,
    borderRadius: 24,
    padding: 32,
    minHeight: 600,
    border: `1px solid ${theme.colors.gray[1]}`,
  },

  profileCard: {
    padding: '24px 20px',
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
}));

function AccountPage() {
  const { classes } = useStyles();
  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [cart, setCart] = useState({});

  useEffect(() => {
    api.get_cart().then((res) => {
      console.log(res.data.data);
      setCart(res.data.data);
    });
  }, []);

  const getData = () => {
    api.get_cart().then((res) => {
      console.log(res.data.data);
      setCart(res.data.data);
    });
  };

  const updateQty = (id, qty) => {
    if (qty <= 1) {
      removeItem(id);
    } else {
      api
        .update_cart({
          item_id: id,
          quantity: qty,
        })
        .then((res) => {
          console.log(res.data);
          if (res.data.success) {
            showSuccessToast({
              title: 'Success',
              message: 'Cart Updated.',
            });
            getData();
          } else {
            showErrorToast({
              title: 'Error',
              message: 'Error updating cart.',
            });
          }
        });
    }
  };

  const removeItem = (id) => {
    api
      .remove_cart_item({
        item_id: id,
      })
      .then((res) => {
        console.log(res.data);
        if (res.data.success) {
          showSuccessToast({
            title: 'Success',
            message: 'Cart Updated.',
          });
          getData();
        } else {
          showErrorToast({
            title: 'Error',
            message: 'Error updating cart.',
          });
        }
      });
  };

  const handleDeleteAccount = async () => {
    await api.delete_user().then(
      res => {
        if (res.success) {
          console.log(res);
          localStorage.clear();
          showSuccessToast({ title: "Success", message: res.message });
          navigate("/login");
        } else {
          showErrorToast({ title: "Error", message: res.message });
        }
      }
    ).catch(err => {
      console.log(err);
    })
  }

  const handleDeleteData = async () => {
    await api.delete_user_data().then(
      res => {
        if (res.success) {
          console.log(res);
          showSuccessToast({ title: "Success", message: res.message });
        } else {
          showErrorToast({ title: "Error", message: res.message });
        }
      }
    ).catch(err => {
      console.log(err);
    })
  }

  const location = useLocation();

  const [activeTab, setActiveTab] = useState(
    location.state?.openTab || "gallery"
  );

  let storedSidebarUser = {};
  try {
    storedSidebarUser = JSON.parse(localStorage.getItem('user') || '{}') || {};
  } catch (err) {
    storedSidebarUser = {};
  }
  const sidebarUserName =
    storedSidebarUser.first_name ||
    storedSidebarUser.name ||
    storedSidebarUser.username ||
    localStorage.getItem('user_first_name') ||
    localStorage.getItem('user_name') ||
    'User';
  const sidebarInitial = sidebarUserName.charAt(0).toUpperCase();

  return (
    <div>
      <Container
        size={'xl'}
        fluid={isMobileScreen}
        px={isMobileScreen && 0}
        style={{ overflow: 'hidden', marginTop: isMobileScreen ? '150px' : '170px' }}
      >
        <Box mb={isMobileScreen ? 14 : 24} px={10}>
          <Breadcrumbs separator="/">
            <Text size={14} color="dimmed">
              Home
            </Text>
            <Text size={14}>Account</Text>
          </Breadcrumbs>
        </Box>

        {/* Categories */}
        <Box p={10}>
          <Flex align={'center'} justify={'space-between'} wrap={"wrap"} gap={10}>
            <Text size={'xl'} fw={600}>
              Account
            </Text>
            {localStorage.getItem('user_token') ? (
              <Button
                variant="filled"
                color="red"
                leftIcon={<IconLogout size={17} />}
                radius="md"
                sx={{
                  backgroundColor: '#d90429',
                  borderColor: '#d90429',
                  color: theme.white,
                  fontWeight: 800,
                  '&:hover': {
                    backgroundColor: '#b00020',
                    borderColor: '#b00020',
                  },
                }}
                onClick={() => {
                  let city_name = localStorage.getItem('city_name');
                  let city_pincode = localStorage.getItem('city_pincode');
                  let city_id = localStorage.getItem('city_id');
                  localStorage.clear();
                  localStorage.setItem('city_name', city_name);
                  localStorage.setItem('city_pincode', city_pincode);
                  localStorage.setItem('city_id', city_id);
                  navigate('/');
                }}
              >
                Logout
              </Button>
            ) : (
              <></>
            )}
          </Flex>
          {/* <Flex w={"100%"} align={"center"} justify={"right"} gap={10} wrap={"wrap"} my={10}>
            <Button color='red' variant='outline' onClick={handleDeleteAccount}>Delete My Account</Button>
          </Flex> */}

          <Box mt={isMobileScreen ? 10 : 30}>
            {isMobileScreen ? (
              <>
                <div className={classes.mobileNavScroll}>
                  {[
                    { id: 'gallery', label: 'Orders', icon: IconReceipt2 },
                    { id: 'streak', label: 'Streak', icon: IconTrophy },
                    { id: 'referral', label: 'Referral', icon: IconShare },
                    { id: 'messages', label: 'Address', icon: IconMapPin },
                    { id: 'settings', label: 'Profile', icon: IconUser },
                    { id: 'feedback', label: 'Feedback', icon: IconMessage2 },
                  ].map((tab) => (
                    <div
                      key={tab.id}
                      className={`${classes.navChip} ${activeTab === tab.id ? classes.activeNavChip : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <tab.icon 
                        className={`${classes.chipIcon} ${activeTab === tab.id ? classes.activeChipIcon : ''}`} 
                      />
                      <Text className={`${classes.chipLabel} ${activeTab === tab.id ? classes.activeChipLabel : ''}`}>
                        {tab.label}
                      </Text>
                    </div>
                  ))}
                </div>
                
                {/* Mobile Content */}
                <Box mt={20} px={10}>
                  {activeTab === 'gallery' && <Orders />}
                  {activeTab === 'streak' && <Streak />}
                  {activeTab === 'referral' && <ReferralCoupon />}
                  {activeTab === 'messages' && <Addresses />}
                  {activeTab === 'settings' && <AccountDetails />}
                  {activeTab === 'feedback' && <Feedback />}
                </Box>
              </>
            ) : (
              <Grid gutter={40} align="stretch">
                <Grid.Col span={3} style={{ display: 'flex' }}>
                  <div className={classes.webSidebarContainer}>
                    <div className={classes.profileCard}>
                      <Box sx={{ 
                        width: 48, 
                        height: 48, 
                        borderRadius: '50%', 
                        background: `linear-gradient(135deg, ${theme.colors.brand[4]}, ${theme.colors.brand[7]})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.white,
                        fontWeight: 700,
                        fontSize: 20
                      }}>
                        {sidebarInitial}
                      </Box>
                      <Box>
                        <Text fw={700} size="sm">{sidebarUserName}</Text>
                        <Text size={12} color="dimmed">Account Settings</Text>
                      </Box>
                    </div>

                    {[
                      { id: 'gallery', label: 'My Orders', icon: IconReceipt2 },
                      { id: 'streak', label: 'My Streak', icon: IconTrophy },
                      { id: 'referral', label: 'Refer & Earn', icon: IconShare },
                      { id: 'messages', label: 'Saved Addresses', icon: IconMapPin },
                      { id: 'settings', label: 'Account Details', icon: IconUser },
                      { id: 'feedback', label: 'Feedback', icon: IconMessage2 },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        className={`${classes.webNavButton} ${activeTab === tab.id ? classes.activeWebNavButton : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        <div className={`${classes.navIconBox} ${activeTab === tab.id ? classes.activeNavIconBox : ''}`}>
                          <tab.icon size={20} className="nav-icon" />
                        </div>
                        <Text className="nav-text" size="sm" weight={500}>{tab.label}</Text>
                        {activeTab === tab.id && (
                          <IconChevronRight size={16} style={{ marginLeft: 'auto', color: theme.colors.brand[6] }} />
                        )}
                      </button>
                    ))}
                  </div>
                </Grid.Col>

                <Grid.Col span={9} style={{ display: 'flex' }}>
                  <div className={classes.contentArea}>
                    <Flex justify="space-between" align="center" mb={30}>
                      <Text size={24} fw={800} color="dark">
                        {{
                          gallery: 'My Orders',
                          streak: 'Activity Streak',
                          referral: 'Referral Rewards',
                          messages: 'Manage Addresses',
                          settings: 'Account Profile',
                          feedback: 'Help & Feedback'
                        }[activeTab]}
                      </Text>
                    </Flex>
                    
                    {activeTab === 'gallery' && <Orders />}
                    {activeTab === 'streak' && <Streak />}
                    {activeTab === 'referral' && <ReferralCoupon />}
                    {activeTab === 'messages' && <Addresses />}
                    {activeTab === 'settings' && <AccountDetails />}
                    {activeTab === 'feedback' && <Feedback />}
                  </div>
                </Grid.Col>
              </Grid>
            )}
          </Box>
        </Box>
      </Container>
    </div>
  );
}

export default AccountPage;
